/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { compose } from "recompose"
import {
  MdSearch,
  MdEdit,
  MdSave,
  MdCancel,
  MdAdd,
  MdDelete,
} from "react-icons/md"
import {
  Heading,
  Box,
  Flex,
  useTheme,
  useToast,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  IconButton,
  Select,
  Switch,
  FormControl,
  FormLabel,
  Text,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  VStack,
  HStack,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Alert,
  AlertIcon,
  Tooltip,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
} from "@chakra-ui/react"
import PropTypes from "prop-types"
import { withFirebase } from "../../utils/Firebase"
import { withAuthorization } from "../../context/Session"

dayjs.extend(utc)

const Products = ({ firebase }) => {
  const theme = useTheme()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [types, setTypes] = useState([])
  const [search, setSearch] = useState("")
  const [showDisabled, setShowDisabled] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [editData, setEditData] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  // Modal form for new product
  const [newProduct, setNewProduct] = useState({
    description: "",
    category: "",
    active: true,
    defaultTaxRate: "04",
    itemId: null, // Will be auto-assigned
    isPackage: false,
    includes: null, // For package breakdowns
    prices: [
      { description: "Precio Standard", value: 0 },
      { description: "Precio Especial", value: 0 },
      { description: "Precio Promocional", value: 0 },
    ],
  })

  // Categories based on backend data
  const serviceCategories = [
    "Cámara Hiperbárica",
    "Binary Facial",
    "Binary Corporal",
    "Depilación",
    "Servicio Estético",
    "Paquetes",
    "Productos Germaine",
    "Productos DSD(Spa capilar)",
    "Productos",
  ]

  useEffect(() => {
    const getTypes = async () => {
      // Get ALL products (both active and inactive) instead of using firebase.getTypes()
      // which only returns active products
      const snapshot = await firebase.firestore.collection("types").get()
      const data = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (a.description > b.description ? 1 : -1))
      setTypes(data)
    }
    if (firebase) {
      getTypes()
    }
  }, [firebase])

  // Helper function to get next available itemId
  const getNextItemId = () => {
    const existingItemIds = types
      .map((type) => type.itemId)
      .filter((id) => id && typeof id === "number")
      .sort((a, b) => b - a) // Sort descending

    // Start from 636 as mentioned in backend context, or use highest + 1
    const nextId =
      existingItemIds.length > 0 ? Math.max(636, existingItemIds[0] + 1) : 636
    return nextId
  }

  // Helper function to normalize text (remove accents and convert to lowercase)
  const normalizeText = (text) => {
    if (!text) return ""
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
  }

  // Calculate category statistics
  const getCategoryStats = () => {
    const stats = {}

    // Initialize stats for predefined categories
    serviceCategories.forEach((category) => {
      stats[category] = 0
    })

    // Count items by normalizing category names
    types.forEach((type) => {
      const typeCategory = type.category || "Sin Categoría"

      // Find matching predefined category using normalized comparison
      const matchingCategory = serviceCategories.find(
        (predefinedCategory) =>
          normalizeText(typeCategory) === normalizeText(predefinedCategory)
      )

      if (matchingCategory) {
        stats[matchingCategory] += 1
      } else {
        // Handle items without category or with unknown categories
        const categoryKey = typeCategory || "Sin Categoría"
        stats[categoryKey] = (stats[categoryKey] || 0) + 1
      }
    })

    return stats
  }

  // Get filtered types based on search, category, and active status
  const getFilteredTypes = () =>
    types?.filter((t) => {
      // Filter by search text (case insensitive and accent insensitive)
      const normalizedSearch = normalizeText(search)
      const matchesSearch =
        !search ||
        normalizeText(t?.description || "").includes(normalizedSearch) ||
        normalizeText(t?.category || "").includes(normalizedSearch)

      // Filter by category (case insensitive and accent insensitive)
      const matchesCategory =
        !selectedCategory ||
        normalizeText(t?.category) === normalizeText(selectedCategory) ||
        (!t?.category && selectedCategory === "Sin Categoría")

      // Filter by active status
      const matchesStatus = showDisabled || t?.active !== false

      return matchesSearch && matchesCategory && matchesStatus
    })

  // CRUD Operations
  const handleEdit = (product) => {
    setEditingId(product.id)

    // Ensure we always have exactly 3 price entries for the UI
    const defaultPrices = [
      { description: "Precio Standard", value: 0 },
      { description: "Precio Especial", value: 0 },
      { description: "Precio Promocional", value: 0 },
    ]

    // Build prices array with exactly 3 items
    const prices = [
      product.prices?.[0] || defaultPrices[0],
      product.prices?.[1] || defaultPrices[1],
      product.prices?.[2] || defaultPrices[2],
    ]

    setEditData({
      description: product.description || "",
      category: product.category || "",
      defaultTaxRate: product.defaultTaxRate || "04",
      active: product.active !== false,
      itemId: product.itemId || null,
      isPackage: !!(product.includes && Array.isArray(product.includes)),
      includes: product.includes || null,
      prices,
    })
  }

  const handleSave = async (productId) => {
    try {
      setIsLoading(true)

      // Parse price values before saving
      const parsedEditData = {
        ...editData,
        prices: editData.prices.map((price) => ({
          ...price,
          value: parseFloat(price.value) || 0,
        })),
      }

      await firebase.firestore
        .collection("types")
        .doc(productId)
        .update({
          ...parsedEditData,
          updatedAt: dayjs().valueOf(),
        })

      // Update local state
      setTypes((prev) =>
        prev.map((type) =>
          type.id === productId ? { ...type, ...parsedEditData } : type
        )
      )

      setEditingId(null)
      setEditData({})

      toast({
        title: "Producto actualizado",
        description: "Los cambios se guardaron correctamente",
        status: "success",
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo actualizar el producto",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditData({})
  }

  const handleToggleActive = async (productId, currentActive) => {
    try {
      const newActiveStatus = !currentActive
      await firebase.firestore.collection("types").doc(productId).update({
        active: newActiveStatus,
        updatedAt: dayjs().valueOf(),
      })

      // Update local state
      setTypes((prev) =>
        prev.map((type) =>
          type.id === productId ? { ...type, active: newActiveStatus } : type
        )
      )

      toast({
        title: newActiveStatus ? "Producto activado" : "Producto desactivado",
        status: "success",
        duration: 2000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo cambiar el estado del producto",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleAddProduct = async () => {
    try {
      setIsLoading(true)
      const newProductRef = firebase.firestore.collection("types").doc()

      // Auto-assign itemId
      const itemId = getNextItemId()

      const productData = {
        ...newProduct,
        itemId,
        // Parse price values before saving
        prices: newProduct.prices.map((price) => ({
          ...price,
          value: parseFloat(price.value) || 0,
        })),
        createdAt: dayjs().valueOf(),
        updatedAt: dayjs().valueOf(),
      }

      // Clean up package-related fields if it's not a package
      if (!newProduct.isPackage) {
        delete productData.includes
      }

      await newProductRef.set(productData)

      // Update local state
      const newProductWithId = { id: newProductRef.id, ...productData }
      setTypes((prev) =>
        [...prev, newProductWithId].sort((a, b) =>
          a.description > b.description ? 1 : -1
        )
      )

      // Reset form
      setNewProduct({
        description: "",
        category: "",
        active: true,
        defaultTaxRate: "04",
        itemId: null,
        isPackage: false,
        includes: null,
        prices: [
          { description: "Precio Standard", value: 0 },
          { description: "Precio Especial", value: 0 },
          { description: "Precio Promocional", value: 0 },
        ],
      })

      onClose()

      toast({
        title: "Producto agregado",
        description: `El nuevo ${
          newProduct.isPackage ? "paquete" : "producto"
        } se creó correctamente con ID: ${itemId}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo crear el producto",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const updateEditData = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }))
  }

  const updateEditPrice = (index, field, value) => {
    setEditData((prev) => {
      const newPrices = [...prev.prices]
      newPrices[index] = { ...newPrices[index], [field]: value }
      return { ...prev, prices: newPrices }
    })
  }

  const updateNewProductPrice = (index, field, value) => {
    setNewProduct((prev) => {
      const newPrices = [...prev.prices]
      newPrices[index] = { ...newPrices[index], [field]: value }
      return { ...prev, prices: newPrices }
    })
  }

  // Package includes management functions
  const addPackageInclude = (isNewProduct = false) => {
    const setter = isNewProduct ? setNewProduct : setEditData
    const currentData = isNewProduct ? newProduct : editData

    const newInclude = {
      id: Date.now(), // Temporary ID for UI purposes
      description: "",
      quantity: 1,
      areas: [],
    }

    setter((prev) => ({
      ...prev,
      includes: [...(prev.includes || []), newInclude],
    }))
  }

  const removePackageInclude = (includeId, isNewProduct = false) => {
    const setter = isNewProduct ? setNewProduct : setEditData
    const currentData = isNewProduct ? newProduct : editData

    setter((prev) => ({
      ...prev,
      includes: prev.includes?.filter((inc) => inc.id !== includeId) || [],
    }))
  }

  const updatePackageInclude = (
    includeId,
    field,
    value,
    isNewProduct = false
  ) => {
    const setter = isNewProduct ? setNewProduct : setEditData

    setter((prev) => ({
      ...prev,
      includes:
        prev.includes?.map((inc) =>
          inc.id === includeId ? { ...inc, [field]: value } : inc
        ) || [],
    }))
  }

  const addAreaToInclude = (includeId, area, isNewProduct = false) => {
    const setter = isNewProduct ? setNewProduct : setEditData

    setter((prev) => ({
      ...prev,
      includes:
        prev.includes?.map((inc) =>
          inc.id === includeId
            ? { ...inc, areas: [...(inc.areas || []), area] }
            : inc
        ) || [],
    }))
  }

  const removeAreaFromInclude = (
    includeId,
    areaIndex,
    isNewProduct = false
  ) => {
    const setter = isNewProduct ? setNewProduct : setEditData

    setter((prev) => ({
      ...prev,
      includes:
        prev.includes?.map((inc) =>
          inc.id === includeId
            ? {
                ...inc,
                areas: inc.areas?.filter((_, i) => i !== areaIndex) || [],
              }
            : inc
        ) || [],
    }))
  }

  // Toggle package mode
  const togglePackageMode = (isNewProduct = false) => {
    const setter = isNewProduct ? setNewProduct : setEditData
    const currentData = isNewProduct ? newProduct : editData

    setter((prev) => ({
      ...prev,
      isPackage: !prev.isPackage,
      includes: !prev.isPackage ? [] : null,
      category:
        !prev.isPackage && prev.category === "" ? "Paquetes" : prev.category,
    }))
  }

  // Package Builder Component
  const PackageBuilder = ({ data, isNewProduct = false }) => {
    const [newAreaInput, setNewAreaInput] = useState("")

    if (!data.isPackage) return null

    const handleAddArea = (includeId) => {
      if (newAreaInput.trim()) {
        addAreaToInclude(includeId, newAreaInput.trim(), isNewProduct)
        setNewAreaInput("")
      }
    }

    return (
      <VStack spacing={4} align="stretch">
        <Alert status="info">
          <AlertIcon />
          <Text fontSize="sm">
            Los paquetes pueden incluir servicios con cantidades (sesiones) o
            áreas de cobertura.
          </Text>
        </Alert>

        <Flex justify="space-between" align="center">
          <Text fontWeight="bold">Incluye en el Paquete:</Text>
          <Button
            size="sm"
            colorScheme="teal"
            variant="outline"
            onClick={() => addPackageInclude(isNewProduct)}
          >
            <MdAdd /> Agregar Servicio
          </Button>
        </Flex>

        {data.includes?.map((include) => (
          <Box key={include.id} p={4} borderWidth={1} borderRadius="md">
            <VStack spacing={3} align="stretch">
              <HStack>
                <FormControl flex={3}>
                  <FormLabel fontSize="sm">Descripción del Servicio</FormLabel>
                  <Input
                    value={include.description}
                    onChange={(e) =>
                      updatePackageInclude(
                        include.id,
                        "description",
                        e.target.value,
                        isNewProduct
                      )
                    }
                    placeholder="Ej: Limpieza Facial Deep Cleansing"
                    size="sm"
                  />
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel fontSize="sm">Cantidad/Sesiones</FormLabel>
                  <NumberInput
                    value={include.quantity}
                    onChange={(valueString) =>
                      updatePackageInclude(
                        include.id,
                        "quantity",
                        parseInt(valueString, 10) || 1,
                        isNewProduct
                      )
                    }
                    min={1}
                    size="sm"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <IconButton
                  icon={<MdDelete />}
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={() => removePackageInclude(include.id, isNewProduct)}
                  aria-label="Eliminar servicio"
                />
              </HStack>

              <FormControl>
                <FormLabel fontSize="sm">
                  Áreas de Cobertura (opcional)
                </FormLabel>
                <HStack mb={2}>
                  <Input
                    value={newAreaInput}
                    onChange={(e) => setNewAreaInput(e.target.value)}
                    placeholder="Ej: Rostro completo, Cuello..."
                    size="sm"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleAddArea(include.id)
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={() => handleAddArea(include.id)}
                    colorScheme="blue"
                    variant="outline"
                  >
                    Agregar
                  </Button>
                </HStack>
                {include.areas?.length > 0 && (
                  <Wrap>
                    {include.areas.map((area, areaIndex) => (
                      <WrapItem key={`${include.id}-area-${area}`}>
                        <Tag size="sm" colorScheme="blue" variant="solid">
                          <TagLabel>{area}</TagLabel>
                          <TagCloseButton
                            onClick={() =>
                              removeAreaFromInclude(
                                include.id,
                                areaIndex,
                                isNewProduct
                              )
                            }
                          />
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                )}
              </FormControl>
            </VStack>
          </Box>
        ))}

        {(!data.includes || data.includes.length === 0) && (
          <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
            No hay servicios incluidos en este paquete. Haz clic en
            &quot;Agregar Servicio&quot; para comenzar.
          </Text>
        )}
      </VStack>
    )
  }

  // PropTypes for PackageBuilder component
  PackageBuilder.propTypes = {
    data: PropTypes.shape({
      isPackage: PropTypes.bool,
      includes: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          description: PropTypes.string,
          quantity: PropTypes.number,
          areas: PropTypes.arrayOf(PropTypes.string),
        })
      ),
    }).isRequired,
    isNewProduct: PropTypes.bool,
  }

  PackageBuilder.defaultProps = {
    isNewProduct: false,
  }

  return (
    <>
      <Box w="100%" h="100%" p={3} overflowY="scroll">
        <Flex justify="space-between" align="center" mb={5}>
          <Heading>Productos</Heading>
          <Button
            leftIcon={<MdAdd />}
            colorScheme="teal"
            onClick={onOpen}
            size="md"
          >
            Agregar Producto
          </Button>
        </Flex>

        <Flex
          mb={5}
          width="100%"
          justifyContent="flex-start"
          align="center"
          gap={4}
          wrap="wrap"
        >
          <InputGroup maxW="400px">
            <InputLeftElement pointerEvents="none" p="0" zIndex="1">
              <MdSearch />
            </InputLeftElement>
            <Input
              placeholder="Buscar productos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>

          <FormControl maxW="250px">
            <FormLabel fontSize="sm" mb={1}>
              Filtrar por Categoría
            </FormLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              size="md"
              placeholder="Todas las categorías"
            >
              {serviceCategories.map((category) => {
                const count = getCategoryStats()[category] || 0
                return (
                  <option key={category} value={category}>
                    {category} ({count})
                  </option>
                )
              })}
              {getCategoryStats()["Sin Categoría"] && (
                <option value="Sin Categoría">
                  Sin Categoría ({getCategoryStats()["Sin Categoría"]})
                </option>
              )}
            </Select>
          </FormControl>

          <FormControl display="flex" alignItems="center" maxW="200px">
            <FormLabel htmlFor="show-disabled" mb="0" fontSize="sm">
              Mostrar inactivos
            </FormLabel>
            <Switch
              id="show-disabled"
              isChecked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
              colorScheme="teal"
            />
          </FormControl>

          {/* Category Statistics Summary */}
          <Box>
            <Text fontSize="sm" color="gray.600">
              Total: {getFilteredTypes()?.length || 0} productos
              {selectedCategory && ` en ${selectedCategory}`}
            </Text>
          </Box>
        </Flex>

        <Box maxH="75vh" overflowY="auto">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Estado</Th>
                <Th>ID</Th>
                <Th>Tipo</Th>
                <Th>Nombre</Th>
                <Th>Categoría</Th>
                <Th>Impuesto</Th>
                <Th>Precio 1</Th>
                <Th>Precio 2</Th>
                <Th>Precio 3</Th>
                <Th>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {getFilteredTypes()?.map((type) => (
                <Tr
                  key={type.id}
                  bg={type.active === false ? "gray.50" : "white"}
                >
                  {/* Status */}
                  <Td>
                    <HStack>
                      <Switch
                        isChecked={type.active !== false}
                        onChange={() =>
                          handleToggleActive(type.id, type.active !== false)
                        }
                        colorScheme="teal"
                        size="sm"
                      />
                      <Badge
                        colorScheme={type.active !== false ? "green" : "red"}
                        size="sm"
                      >
                        {type.active !== false ? "Activo" : "Inactivo"}
                      </Badge>
                    </HStack>
                  </Td>

                  {/* ItemId */}
                  <Td>
                    <Text fontSize="sm" fontFamily="mono">
                      {type.itemId || "N/A"}
                    </Text>
                  </Td>

                  {/* Type (Package/Service) */}
                  <Td>
                    <VStack spacing={1} align="start">
                      <Badge
                        colorScheme={
                          type.includes && Array.isArray(type.includes)
                            ? "purple"
                            : "blue"
                        }
                        variant="solid"
                      >
                        {type.includes && Array.isArray(type.includes)
                          ? "Paquete"
                          : "Servicio"}
                      </Badge>
                      {type.includes && Array.isArray(type.includes) && (
                        <Text fontSize="xs" color="gray.600">
                          {type.includes.length} item
                          {type.includes.length !== 1 ? "s" : ""}
                        </Text>
                      )}
                    </VStack>
                  </Td>

                  {/* Name */}
                  <Td>
                    {editingId === type.id ? (
                      <Input
                        value={editData.description}
                        onChange={(e) =>
                          updateEditData("description", e.target.value)
                        }
                        size="sm"
                      />
                    ) : (
                      <VStack spacing={1} align="start">
                        <Text>{type.description}</Text>
                        {type.includes && Array.isArray(type.includes) && (
                          <Accordion allowToggle size="sm">
                            <AccordionItem border="none">
                              <AccordionButton p={1} minH="auto">
                                <Text fontSize="xs" color="blue.600">
                                  Ver contenido del paquete
                                </Text>
                                <AccordionIcon />
                              </AccordionButton>
                              <AccordionPanel pb={2} px={1}>
                                <VStack spacing={1} align="start">
                                  {type.includes.map((include, idx) => (
                                    <HStack key={include.id || idx} spacing={2}>
                                      <Text fontSize="xs">
                                        {include.quantity}x{" "}
                                        {include.description}
                                      </Text>
                                      {include.areas &&
                                        include.areas.length > 0 && (
                                          <Wrap spacing={1}>
                                            {include.areas.map(
                                              (area, areaIdx) => (
                                                <WrapItem
                                                  key={`${include.id}-area-${area}`}
                                                >
                                                  <Tag
                                                    size="xs"
                                                    colorScheme="gray"
                                                  >
                                                    {area}
                                                  </Tag>
                                                </WrapItem>
                                              )
                                            )}
                                          </Wrap>
                                        )}
                                    </HStack>
                                  ))}
                                </VStack>
                              </AccordionPanel>
                            </AccordionItem>
                          </Accordion>
                        )}
                      </VStack>
                    )}
                  </Td>

                  {/* Category */}
                  <Td>
                    {editingId === type.id ? (
                      <Input
                        value={editData.category}
                        onChange={(e) =>
                          updateEditData("category", e.target.value)
                        }
                        size="sm"
                      />
                    ) : (
                      <Text>{type?.category || "N/A"}</Text>
                    )}
                  </Td>

                  {/* Tax Rate */}
                  <Td>
                    {editingId === type.id ? (
                      <Select
                        value={editData.defaultTaxRate}
                        onChange={(e) =>
                          updateEditData("defaultTaxRate", e.target.value)
                        }
                        size="sm"
                        maxW="80px"
                      >
                        <option value="04">4%</option>
                        <option value="08">13%</option>
                      </Select>
                    ) : (
                      <Badge
                        colorScheme={
                          type?.defaultTaxRate === "08" ? "red" : "blue"
                        }
                      >
                        {type?.defaultTaxRate === "08" ? "13%" : "4%"}
                      </Badge>
                    )}
                  </Td>

                  {/* Prices */}
                  {[
                    { index: 0, key: "price-standard" },
                    { index: 1, key: "price-special" },
                    { index: 2, key: "price-promotional" },
                  ].map(({ index, key }) => (
                    <Td key={key}>
                      {editingId === type.id ? (
                        <VStack spacing={1} align="stretch">
                          <Input
                            value={editData.prices?.[index]?.description || ""}
                            onChange={(e) =>
                              updateEditPrice(
                                index,
                                "description",
                                e.target.value
                              )
                            }
                            placeholder="Descripción"
                            size="sm"
                          />
                          <Input
                            value={editData.prices?.[index]?.value || ""}
                            onChange={(e) =>
                              updateEditPrice(index, "value", e.target.value)
                            }
                            type="number"
                            placeholder="0"
                            size="sm"
                          />
                        </VStack>
                      ) : (
                        <VStack spacing={0} align="start">
                          <Text fontSize="sm" color="gray.600">
                            {type?.prices?.[index]?.description || "N/A"}
                          </Text>
                          <Text fontWeight="bold">
                            ₡
                            {type?.prices?.[index]?.value?.toLocaleString() ||
                              "0"}
                          </Text>
                        </VStack>
                      )}
                    </Td>
                  ))}

                  {/* Actions */}
                  <Td>
                    <HStack spacing={2}>
                      {editingId === type.id ? (
                        <>
                          <IconButton
                            icon={<MdSave />}
                            size="sm"
                            colorScheme="green"
                            onClick={() => handleSave(type.id)}
                            isLoading={isLoading}
                          />
                          <IconButton
                            icon={<MdCancel />}
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={handleCancel}
                          />
                        </>
                      ) : (
                        <IconButton
                          icon={<MdEdit />}
                          size="sm"
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => handleEdit(type)}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>

      {/* Add Product Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Agregar Nuevo Producto</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Nombre del Producto</FormLabel>
                <Input
                  value={newProduct.description}
                  onChange={(e) =>
                    setNewProduct((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Ej: Consulta General"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Categoría</FormLabel>
                <Select
                  value={newProduct.category}
                  onChange={(e) =>
                    setNewProduct((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                  placeholder="Seleccionar categoría"
                >
                  {serviceCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <HStack justify="space-between" align="center">
                  <FormLabel mb="0">¿Es un paquete?</FormLabel>
                  <Switch
                    isChecked={newProduct.isPackage}
                    onChange={() => togglePackageMode(true)}
                    colorScheme="purple"
                  />
                </HStack>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Los paquetes pueden incluir múltiples servicios con cantidades
                  y áreas específicas
                </Text>
              </FormControl>

              {newProduct.isPackage && (
                <Box w="100%">
                  <Divider my={4} />
                  <PackageBuilder data={newProduct} isNewProduct />
                  <Divider my={4} />
                </Box>
              )}

              <FormControl>
                <FormLabel>Impuesto por Defecto</FormLabel>
                <Select
                  value={newProduct.defaultTaxRate}
                  onChange={(e) =>
                    setNewProduct((prev) => ({
                      ...prev,
                      defaultTaxRate: e.target.value,
                    }))
                  }
                >
                  <option value="04">4% - Tarifa reducida</option>
                  <option value="08">13% - Tarifa general</option>
                </Select>
              </FormControl>

              {newProduct.prices.map((price, index) => (
                <FormControl key={price.description || `price-${index}`}>
                  <FormLabel>Precio {index + 1}</FormLabel>
                  <HStack>
                    <Input
                      value={price.description}
                      onChange={(e) =>
                        updateNewProductPrice(
                          index,
                          "description",
                          e.target.value
                        )
                      }
                      placeholder="Descripción del precio"
                      flex={2}
                    />
                    <Input
                      value={price.value}
                      onChange={(e) =>
                        updateNewProductPrice(index, "value", e.target.value)
                      }
                      type="number"
                      placeholder="0"
                      flex={1}
                    />
                  </HStack>
                </FormControl>
              ))}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="teal"
              onClick={handleAddProduct}
              isLoading={isLoading}
            >
              Crear Producto
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

Products.propTypes = {
  firebase: PropTypes.shape().isRequired,
}

const condition = (authUser) => authUser
const ComposedProducts = compose(
  withFirebase,
  withAuthorization(condition)
)(Products)
export default ComposedProducts
